import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarRange, CalendarDays, Calendar, SlidersHorizontal } from "lucide-react";
import { NeuButton, NeuCard, NeuInput } from "../../components/neu/Neu";
import { NeuPicker } from "../../components/neu/NeuPicker";
import { DateInput } from "../../components/neu/DateInput";
import { listBranches, listUsers } from "../../api/directory";

const shiftMonth = (delta) => {
  const now = new Date();
  now.setMonth(now.getMonth() + delta);
  return now.toISOString().slice(0, 7);
};

// أنماط الفترة: الشهر الحالي · الشهر السابق · شهر محاسبي · فترة مخصصة (حقول التواريخ تظهر هنا فقط).
export const PayrollFilters = ({ value, onChange }) => {
  const { t } = useTranslation();
  const [branches, setBranches] = useState([]);
  const [people, setPeople] = useState([]);
  const [mode, setMode] = useState(value.start && value.end ? "custom" : "this");

  useEffect(() => {
    listBranches().then(setBranches).catch(() => setBranches([]));
  }, []);

  useEffect(() => {
    const b = value.branch_id && value.branch_id !== "all" ? value.branch_id : undefined;
    listUsers(b).then((d) => setPeople(Array.isArray(d) ? d : d.items || [])).catch(() => setPeople([]));
  }, [value.branch_id]);

  const set = (patch) => onChange({ ...value, ...patch });
  const pick = (next) => {
    setMode(next);
    if (next === "this") set({ month: shiftMonth(0), start: "", end: "" });
    if (next === "last") set({ month: shiftMonth(-1), start: "", end: "" });
    if (next === "month") set({ start: "", end: "" });
  };

  const modes = [
    { key: "this", label: t("payrollFilters.thisMonth"), icon: CalendarDays },
    { key: "last", label: t("payrollFilters.lastMonth"), icon: Calendar },
    { key: "month", label: t("payrollFilters.accountingMonth"), icon: CalendarRange },
    { key: "custom", label: t("payrollFilters.customPeriod"), icon: SlidersHorizontal },
  ];

  return (
    <NeuCard className="grid gap-4" data-testid="payroll-filters">
      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
        {modes.map(({ key, label, icon: Icon }) => (
          <NeuButton key={key} data-testid={`payroll-mode-${key}`} variant={mode === key ? "primary" : "default"}
                     className="flex items-center justify-center gap-2 px-3 py-2 text-xs"
                     onClick={() => pick(key)}>
            <Icon className="h-4 w-4 shrink-0" /> {label}
          </NeuButton>
        ))}
      </div>

      <div className="grid gap-3">
        {mode === "month" ? (
          <div data-testid="payroll-month-field">
            <NeuInput type="month" className="w-full" data-testid="payroll-month" value={value.month}
                      onChange={(e) => set({ month: e.target.value, start: "", end: "" })} />
          </div>
        ) : null}
        {mode === "custom" ? (
          <div className="grid grid-cols-2 gap-3" data-testid="payroll-custom-fields">
            <DateInput className="w-full" label={t("payrollFilters.from")} testId="payroll-start"
                       value={value.start} onChange={(v) => set({ start: v })} />
            <DateInput className="w-full" label={t("payrollFilters.to")} testId="payroll-end"
                       value={value.end} onChange={(v) => set({ end: v })} />
          </div>
        ) : null}
        <NeuPicker className="w-full" testId="payroll-branch"
                   value={value.branch_id} onChange={(e) => set({ branch_id: e.target.value })}>
          <option value="">{t("payrollFilters.selectBranch")}</option>
          <option value="all">{t("payrollFilters.allBranches")}</option>
          {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </NeuPicker>
        <NeuPicker className="w-full" testId="payroll-employee"
                   value={value.user_id} onChange={(e) => set({ user_id: e.target.value })}>
          <option value="">{t("payrollFilters.selectEmployee")}</option>
          <option value="all">{t("payrollFilters.allEmployees")}</option>
          {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </NeuPicker>
      </div>
    </NeuCard>
  );
};
